import { BoardCommand } from 'src/components/dispatch/assignment/types';

/**
 * D&D の成功通知。出す / 出さないの判定ごとここに置く（`autoDispatchNotification.ts` と同じ作り）。
 *
 * **出すのは自動配車が完了してから**（ユーザー指摘 2026-09-29）。落とした瞬間に盤面は
 * 変わるが、それは楽観反映で、実際に割り付くのはサーバの自動配車が終わってから
 * （`useDispatchAssignment` は最後の操作から 2000ms 待って撃つ）。落とした時点で
 * 「割り付けました」と出すと、まだ何も起きていない時点で成功を言い切ることになる。
 *
 * それでも通知を出すのは、何件がどこへ入ったのかがバーの見た目だけでは読めないため
 * （複数選択をまとめて落とすと 1 本のバーに束ねられる）。文言はサンプル（route-planner
 * `pages/MatchingPresenter/index.tsx:878-902`）の「{地点名} を割り付けました」に合わせる。
 *
 * Why not すべてのドロップで出す: 運行バーをレーン間で移動しただけ・未割当へ戻しただけは
 * 「割り付けた」ではない。バーを並べ替えるたびにトーストが並ぶと、本当に割り付いた
 * ときの通知が埋もれる（`autoDispatchNotification` が入れ替え・移動を黙らせているのと同じ理由）。
 */
export type DropAssignNotification = {
  message: string;
  variant?: 'success' | 'error';
};

/**
 * 完了を待つあいだ貯めておく、割り付けたぶん。
 *
 * 名前を落とした時点で持つのは、`applyCommand` が落とした案件を未割当リストからすぐ
 * 消すため。完了してから引くと文言の地点名だけが空になる。
 *
 * Why not 名前を別の配列で持つ: 割り付かなかったぶんを後から外すので、どの名前が
 * どの案件のものかが要る。名前を引けない案件があると位置がずれ、外したあとに
 * 別の案件の名前を出してしまう。
 */
export type DroppedItem = { legRunId: number; name: string | null };
export type DroppedAssignment = { items: DroppedItem[] };

/**
 * 落とした 1 操作を貯めへ反映する。
 *
 * 自動配車は落とすたびに撃つのではなく最後の操作からしばらく待って 1 回だけ撃つので、
 * その待ち時間のあいだの操作をここで積み上げる。
 *
 * Why not 足すだけ（旧 `droppedAssignment` + `mergeDropped`）: 待ち時間のあいだに
 * 「落とす先を間違えて戻し、別の車両へ落とし直す」という直し方をされると、足すだけでは
 * 同じ案件が 2 件に数えられ、1 件しか動かしていないのに「2件を割り付けました」と出る。
 * 落としたあと自分で戻した案件も残り、取り消したものまで成功に数える
 * （Codex レビュー指摘 2026-09-29）。落とす操作と戻す操作の両方をここへ通す。
 *
 * @param pending いままでに貯めたぶん
 * @param command 落とした結果のコマンド。`buildBoardCommand` が null を返したらドロップは無効
 * @param nameByLegRunId 割り付けた案件の地点名。1 件のときだけ文言に出す。引けないものは入れない
 */
export function trackDrop(
  pending: DroppedAssignment | null,
  command: BoardCommand | null,
  nameByLegRunId: ReadonlyMap<number, string>,
): DroppedAssignment | null {
  if (!command) return pending;
  const current = pending?.items ?? [];

  // 未割当へ戻したぶんは「割り付けた」ではなくなる
  if (command.kind === 'unassign') {
    const removed = new Set(command.legRunIds);
    const items = current.filter((item) => !removed.has(item.legRunId));
    return items.length > 0 ? { items } : null;
  }

  // 未割当からの投入だけが「割り付けた」。運行バーの移動（scope: 'trip'）は数えない
  if (command.kind !== 'assign' || command.source.scope !== 'unassigned') return pending;
  if (command.legRunIds.length === 0) return pending;

  // 同じ案件は 1 件に畳む。落とし直しは最後の操作で上書きする（地点名も新しい方を採る）
  const added = new Set(command.legRunIds);
  const items = [
    ...current.filter((item) => !added.has(item.legRunId)),
    ...command.legRunIds.map((legRunId) => ({
      legRunId, name: nameByLegRunId.get(legRunId) ?? null,
    })),
  ];
  return { items };
}

/**
 * 割り付かなかったぶんを外す。1 件も残らなければ null。
 *
 * 落とした案件が自動配車で置けなかったとき、共有フック（`useDispatchAssignment`）は
 * その legRun を未割当へ戻す。外さずに数えると、カードが未割当リストへ戻っているのに
 * 「割り付けました」と言い切ることになる（Codex レビュー指摘 2026-09-29）。
 *
 * `notAllocatedOrderIds` には落としていない案件も混ざる（同じ実行で他の案件も送られる）が、
 * こちらが持っている legRunId としか突き合わせないので影響しない。
 */
export function allocatedDropped(
  dropped: DroppedAssignment | null,
  notAllocatedOrderIds: readonly number[],
): DroppedAssignment | null {
  if (!dropped) return null;
  const notAllocated = new Set(notAllocatedOrderIds);
  const items = dropped.items.filter((item) => !notAllocated.has(item.legRunId));
  return items.length > 0 ? { items } : null;
}

/** 貯めたぶんを文言にする。貯まっていなければ出さない */
export function dropAssignNotification(
  dropped: DroppedAssignment | null,
): DropAssignNotification | null {
  if (!dropped) return null;
  const count = dropped.items.length;
  if (count === 0) return null;
  const name = count === 1 ? dropped.items[0].name : null;
  if (name) return { message: `${name} を割り付けました`, variant: 'success' };
  return { message: `${count.toLocaleString()}件を割り付けました`, variant: 'success' };
}
